import * as React from 'react';
import {Suspense, lazy, useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams} from 'react-router-dom';
import {AppLayout} from './components/AppLayout';
import {TenantLayout} from './components/TenantLayout';
import {NotFoundPage} from './pages/NotFoundPage';
import {ProtectedRoute, RoleGuard} from './components/ProtectedRoute';
import {initGA, logPageView} from './utils/analytics';
import {ThemeProvider} from "./contexts/ThemeContext";
import {ConfirmProvider} from './contexts/ConfirmContext';
import {HomePage} from './pages/HomePage';
import {ErrorBoundary} from './components/UI/ErrorBoundary';
import {LoadingSpinner} from './components/UI/LoadingSpinner';

/* Mỗi trang là một chunk riêng.
   ------------------------------------------------------------------
   Trước đây cả app là MỘT file 377 KB (gzip): ai mở trang đăng nhập cũng phải
   tải kèm recharts, yup và html-to-image - thư viện chỉ dùng ở màn thống kê và
   màn chụp hóa đơn. Tách theo route thì chunk đầu tiên chỉ còn khung app +
   trang chủ, phần còn lại tải đúng lúc vào trang cần.

   HomePage và NotFoundPage KHÔNG tách: đó là hai trang vào thẳng được bằng URL
   khi chưa có gì trong cache, tách ra chỉ thêm một vòng mạng trước lần vẽ đầu. */
const LandlordDashboardPage = lazy(() => import('./pages/Landlord/LandlordDashboardPage').then(m => ({default: m.LandlordDashboardPage})));
const RoomDetailPage = lazy(() => import('./pages/Landlord/RoomDetailPage').then(m => ({default: m.RoomDetailPage})));
const UtilitiesRecordPage = lazy(() => import('./pages/Landlord/UtilitiesRecordPage').then(m => ({default: m.UtilitiesRecordPage})));
const RoomUtilitiesPage = lazy(() => import('./pages/Landlord/RoomUtilitiesPage').then(m => ({default: m.RoomUtilitiesPage})));
const StatisticsPage = lazy(() => import('./pages/Landlord/StatisticsPage').then(m => ({default: m.StatisticsPage})));
const RoomStatisticsPage = lazy(() => import('./pages/Landlord/RoomStatisticsPage').then(m => ({default: m.RoomStatisticsPage})));
const TenantUtilitiesPage = lazy(() => import('./pages/Tenant/TenantUtilitiesPage').then(m => ({default: m.TenantUtilitiesPage})));
const TenantGuidePage = lazy(() => import('./pages/Tenant/TenantGuidePage').then(m => ({default: m.TenantGuidePage})));
const BankingInfoPage = lazy(() => import('./pages/Tenant/BankingInfoPage').then(m => ({default: m.BankingInfoPage})));
const SettingsPage = lazy(() => import('./pages/Admin/SettingsPage').then(m => ({default: m.SettingsPage})));
const PermissionsPage = lazy(() => import('./pages/Admin/PermissionsPage').then(m => ({default: m.PermissionsPage})));

/* /chunha -> /phong.
   Đường dẫn cũ đọc ra là "chủ nhà" - nói về NGƯỜI đang bấm, trong khi trang đó
   nói về PHÒNG, y hệt /diennuoc nói về việc. Đổi rồi thì /phong/201 và
   /diennuoc/201 đọc thành một cặp: cùng một phòng, hai việc.

   Giữ lại đường cũ vì nó đã đi ra khỏi app: link dán cho nhau, dấu trang, và
   icon "thêm vào màn hình chính" (iOS chụp nguyên URL đang mở, không dùng
   start_url của manifest - xem useUrlState). Bỏ hẳn là những cái đó ra trang
   404. `replace` để bấm Back không rơi ngược vào chính cú chuyển hướng. */
const LegacyRoomRedirect: React.FC = () => {
    const {roomId} = useParams<{ roomId?: string }>();
    const {search} = useLocation();
    return <Navigate to={`/phong${roomId ? `/${roomId}` : ''}${search}`} replace/>;
};

// Component để track page views
const PageTracker: React.FC = () => {
    const location = useLocation();

    useEffect(() => {
        logPageView();
    }, [location]);

    return null;
};

function App() {
    useEffect(() => {
        initGA('G-8QLXDCCL8P');
    }, []);

    return (
        <ErrorBoundary>
            <ThemeProvider>
                {/* Hộp xác nhận dùng chung - trên cả Router vì trang chủ và các
                    trang công khai nằm ngoài mọi khung layout nhưng vẫn hỏi. */}
                <ConfirmProvider>
                    <Router>
                        <PageTracker/>
                        {/* Suspense đặt NGOÀI <Routes>: đặt trong từng route thì mỗi
                            lần chuyển trang React lại tháo cả cây con xuống rồi dựng
                            lại, làm mất trạng thái cuộn. */}
                        <Suspense fallback={<LoadingSpinner message="Đang mở trang..."/>}>
                            <Routes>
                                {/* Đường dẫn cũ - chuyển hướng trước cả lớp đăng nhập,
                                    nó chỉ đổi địa chỉ chứ không đọc gì của ai */}
                                <Route path="/chunha" element={<LegacyRoomRedirect/>}/>
                                <Route path="/chunha/:roomId" element={<LegacyRoomRedirect/>}/>

                                {/* Public Route with No AppLayout */}
                                <Route path="/" element={<HomePage/>}/>
                                <Route path="/home" element={<HomePage/>}/>
                                <Route path="/huong-dan" element={<TenantGuidePage/>}/>
                                <Route path="/huongdan" element={<Navigate to="/huong-dan" replace/>}/>
                                <Route path="/guide" element={<Navigate to="/huong-dan" replace/>}/>
                                <Route path="/nganhang" element={<BankingInfoPage/>}/>
                                <Route path="404" element={<NotFoundPage/>}/>

                                {/* Người thuê - khung riêng, không sidebar */}
                                <Route element={
                                    <ProtectedRoute>
                                        <TenantLayout/>
                                    </ProtectedRoute>
                                }>
                                    <Route path="/phong-cua-toi" element={<RoleGuard allow={['tenant']}><TenantUtilitiesPage/></RoleGuard>}/>
                                </Route>

                                {/* Chủ nhà / quản lý */}
                                <Route element={
                                    <ProtectedRoute>
                                        <AppLayout/>
                                    </ProtectedRoute>
                                }>
                                    <Route path="/phong" element={<RoleGuard allow={['admin', 'manager', 'viewer']}><LandlordDashboardPage/></RoleGuard>}/>
                                    <Route path="/phong/:roomId" element={<RoleGuard allow={['admin', 'manager', 'viewer']}><RoomDetailPage/></RoleGuard>}/>
                                    <Route path="/diennuoc" element={<RoleGuard allow={['admin', 'manager', 'viewer']}><UtilitiesRecordPage/></RoleGuard>}/>
                                    {/* Ghi số của MỘT phòng. Cùng cặp trang như /phong:
                                        danh sách ở đường trần, từng phòng có địa chỉ riêng
                                        để gửi link, tải lại và bấm Back đều đúng chỗ. */}
                                    <Route path="/diennuoc/:roomId" element={<RoleGuard allow={['admin', 'manager', 'viewer']}><RoomUtilitiesPage/></RoleGuard>}/>
                                    <Route path="/thongke" element={<RoleGuard allow={['admin', 'manager', 'viewer']}><StatisticsPage/></RoleGuard>}/>
                                    {/* Lịch sử & bản ghi của MỘT phòng - mặt thứ ba của
                                        cùng một phòng, cùng cặp trang như /phong và
                                        /diennuoc: danh sách ở đường trần, phòng có địa
                                        chỉ riêng. */}
                                    <Route path="/thongke/:roomId" element={<RoleGuard allow={['admin', 'manager', 'viewer']}><RoomStatisticsPage/></RoleGuard>}/>
                                    {/* Quản lý vào được Cài đặt để sửa giá mặc định; thêm/xóa phòng
                                        và tài khoản ngân hàng vẫn chỉ Admin (chặn thật ở rules) */}
                                    <Route path="/caidat" element={<RoleGuard allow={['admin', 'manager']}><SettingsPage/></RoleGuard>}/>
                                    {/* Quản lý vào được để duyệt đăng ký và cho người thuê vào phòng;
                                        tab "Chủ nhà" (cấp quyền admin/quản lý/chỉ xem) chỉ Admin thấy,
                                        và rules cũng chỉ cho Quản lý đụng vào dòng vai trò 'tenant' */}
                                    <Route path="/phanquyen" element={<RoleGuard allow={['admin', 'manager']}><PermissionsPage/></RoleGuard>}/>
                                </Route>

                                {/* URL không khớp route nào - tránh render trang trắng */}
                                <Route path="*" element={<NotFoundPage/>}/>
                            </Routes>
                        </Suspense>
                    </Router>
                </ConfirmProvider>
                {/* Nhãn môi trường: nằm GIỮA mép trên, góc trái là nút mở menu.
                    pointer-events-none để không bao giờ ăn mất cú bấm nào. */}
                {process.env.REACT_APP_ENV === 'dev' && (
                    <div
                        className="fixed top-1 left-1/2 -translate-x-1/2 pointer-events-none
                            bg-rose-500 text-white font-bold text-xs px-3 py-1 rounded-full opacity-80 z-50">
                        Dev
                    </div>
                )}
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
